import { useEffect, useMemo, useState } from 'react'
import {
  discountModeToApplyEachPayment,
  resolveCrmLinkDiscountMode,
  type CrmLinkDiscountMode,
} from '../../../../../discountMode.utils'
import {
  DEFAULT_MONTHLY_COURSE_PERIOD_UNIT,
  getMonthlyCourseLinkPeriodBasePrice,
  getMonthlyCourseLinkSuggestedPrice,
  isMonthlyCourseLinkTariff,
  type MonthlyCourseLinkPeriodUnit,
} from '../../../../../monthlyCourseLinkDiscount.utils'
import type { ICourseEnrollmentItem } from '../types'

type OnChange = (
  key: keyof ICourseEnrollmentItem,
  value: number | string | boolean | null | object,
) => void

type UseMonthlyAdditionalCourseFormParams = {
  order: ICourseEnrollmentItem
  selectedTariff?: ICourseTariff | null
  linkEnrollDate?: string | null
  defaultDiscount?: number | null
  onChange: OnChange
}

/**
 * Оркестрация помесячного доп. курса: sync period/price, toggle фиксации, discount_mode.
 */
export const useMonthlyAdditionalCourseForm = ({
  order,
  selectedTariff,
  linkEnrollDate,
  defaultDiscount = null,
  onChange,
}: UseMonthlyAdditionalCourseFormParams) => {
  const [priceManuallyEdited, setPriceManuallyEdited] = useState(
    order.price !== null && order.price !== undefined,
  )

  const isActive = isMonthlyCourseLinkTariff(selectedTariff?.type)
  const isPeriodFixed = Boolean(order.is_period_fixed)
  const periodUnit: MonthlyCourseLinkPeriodUnit =
    order.period_unit === 'day' ? 'day' : DEFAULT_MONTHLY_COURSE_PERIOD_UNIT
  const hasPeriodValue =
    order.value != null &&
    Number.isFinite(Number(order.value)) &&
    Number(order.value) >= 1
  const periodValue = hasPeriodValue
    ? Math.trunc(Number(order.value))
    : null
  const discountValue = Math.max(0, Math.trunc(Number(order.discount) || 0))
  const discountMode = resolveCrmLinkDiscountMode({
    discount: discountValue,
    discountMode: order.discount_mode,
    applyDiscountEachPayment: order.apply_discount_each_payment,
    isPeriodFixed,
  })
  const applyDiscountEachPayment =
    discountValue > 0 && discountMode === 'every_payment'
  const calculationEnrollDate = order.enrollDate ?? linkEnrollDate

  const fullBasePrice = useMemo(
    () =>
      getMonthlyCourseLinkPeriodBasePrice(
        selectedTariff,
        periodUnit,
        periodValue ?? 1,
        calculationEnrollDate,
      ),
    [calculationEnrollDate, periodUnit, periodValue, selectedTariff],
  )

  const suggestedPrice = getMonthlyCourseLinkSuggestedPrice(
    selectedTariff,
    periodUnit,
    periodValue ?? 1,
    discountValue,
    calculationEnrollDate,
    applyDiscountEachPayment,
    order.discount_mode,
    isPeriodFixed,
  )

  useEffect(() => {
    if (!isActive || !selectedTariff) return

    if (discountValue <= 0 && (order.apply_discount_each_payment || order.discount_mode)) {
      onChange('apply_discount_each_payment', false)
      onChange('discount_mode', null)
    }

    if (isPeriodFixed) {
      if (!order.period_unit) {
        onChange('period_unit', DEFAULT_MONTHLY_COURSE_PERIOD_UNIT)
      }
    } else if (order.period_unit != null) {
      onChange('period_unit', null)
    }
    if (!isPeriodFixed && (!order.value || Number(order.value) < 1)) {
      onChange('value', 1)
    }
    if (!isPeriodFixed || !priceManuallyEdited) {
      if (order.price !== suggestedPrice) {
        onChange('price', suggestedPrice)
      }
    }
  }, [
    isActive,
    selectedTariff,
    isPeriodFixed,
    order.period_unit,
    order.value,
    order.price,
    order.apply_discount_each_payment,
    order.discount_mode,
    discountValue,
    suggestedPrice,
    priceManuallyEdited,
    onChange,
  ])

  const onTogglePeriodFixed = (checked?: boolean) => {
    const nextChecked =
      typeof checked === 'boolean' ? checked : !isPeriodFixed
    setPriceManuallyEdited(false)
    onChange('is_period_fixed', nextChecked)
    onChange(
      'period_unit',
      nextChecked ? DEFAULT_MONTHLY_COURSE_PERIOD_UNIT : null,
    )
    const nextValue = Math.max(1, Math.trunc(Number(order.value) || 1))
    onChange('value', nextValue)

    const nextMode: CrmLinkDiscountMode | null =
      discountValue > 0
        ? nextChecked
          ? 'renewal_only'
          : 'first_only'
        : null
    onChange('discount_mode', nextMode)
    onChange(
      'apply_discount_each_payment',
      discountModeToApplyEachPayment(nextMode),
    )
    onChange(
      'price',
      getMonthlyCourseLinkSuggestedPrice(
        selectedTariff,
        DEFAULT_MONTHLY_COURSE_PERIOD_UNIT,
        nextValue,
        discountValue,
        calculationEnrollDate,
        false,
        nextMode,
        nextChecked,
      ),
    )
  }

  const onChangePeriodUnit = (value: MonthlyCourseLinkPeriodUnit) => {
    onChange('period_unit', value)
  }

  const onChangeValue = (value: string | number | null) => {
    if (value === null || value === undefined || value === '') {
      onChange('value', null)
      return
    }
    onChange('value', Math.max(1, Math.trunc(Number(value) || 0)))
  }

  const onChangeUnfixedValue = (value: number) => {
    onChange('value', Math.max(1, Math.trunc(Number(value) || 1)))
  }

  const onChangePrice = (value: string | number | null) => {
    if (!isPeriodFixed) return
    if (value === null || value === undefined || value === '') {
      setPriceManuallyEdited(true)
      onChange('price', null)
      return
    }
    setPriceManuallyEdited(true)
    onChange('price', Math.max(0, Number(value) || 0))
  }

  const onChangeDiscount = (value: string | number | null) => {
    if (value === null || value === undefined || value === '') {
      setPriceManuallyEdited(false)
      onChange('discount', null)
      onChange('discount_mode', null)
      onChange('apply_discount_each_payment', false)
      return
    }
    const nextDiscount = Math.max(0, Math.trunc(Number(value) || 0))
    setPriceManuallyEdited(false)
    onChange('discount', nextDiscount || null)
    if (nextDiscount <= 0) {
      onChange('discount_mode', null)
      onChange('apply_discount_each_payment', false)
      return
    }
    const nextMode = resolveCrmLinkDiscountMode({
      discount: nextDiscount,
      discountMode: isPeriodFixed ? 'renewal_only' : order.discount_mode ?? 'first_only',
      isPeriodFixed,
    })
    onChange('discount_mode', nextMode)
    onChange(
      'apply_discount_each_payment',
      discountModeToApplyEachPayment(nextMode),
    )
  }

  const onDiscountModeChange = (mode: CrmLinkDiscountMode | null) => {
    const nextMode = resolveCrmLinkDiscountMode({
      discount: discountValue,
      discountMode: mode,
      isPeriodFixed,
    })
    onChange('discount_mode', nextMode)
    onChange(
      'apply_discount_each_payment',
      discountModeToApplyEachPayment(nextMode),
    )
  }

  const onResetSuggestedPrice = () => {
    setPriceManuallyEdited(false)
    onChange('price', suggestedPrice)
  }

  const applyTariffSelection = (tariff: ICourseTariff) => {
    const nextDiscount =
      order.discount === null || order.discount === undefined
        ? defaultDiscount
        : Math.max(0, Math.trunc(Number(order.discount) || 0)) || null
    setPriceManuallyEdited(false)
    onChange('is_period_fixed', false)
    onChange('value', 1)
    onChange('period_unit', null)
    onChange('discount', nextDiscount)
    onChange('discount_mode', nextDiscount ? 'first_only' : null)
    onChange('apply_discount_each_payment', false)
    onChange(
      'price',
      getMonthlyCourseLinkSuggestedPrice(
        tariff,
        DEFAULT_MONTHLY_COURSE_PERIOD_UNIT,
        1,
        nextDiscount ?? 0,
        calculationEnrollDate,
        false,
        nextDiscount ? 'first_only' : null,
        false,
      ),
    )
  }

  const resetManualPrice = () => setPriceManuallyEdited(false)

  return {
    isActive,
    isPeriodFixed,
    periodUnit,
    periodValue,
    discountValue,
    discountMode,
    applyDiscountEachPayment,
    calculationEnrollDate,
    fullBasePrice,
    suggestedPrice,
    priceManuallyEdited,
    displayPrice: Number(order.price) || suggestedPrice,
    onTogglePeriodFixed,
    onChangePeriodUnit,
    onChangeValue,
    onChangeUnfixedValue,
    onChangePrice,
    onChangeDiscount,
    onDiscountModeChange,
    onResetSuggestedPrice,
    applyTariffSelection,
    resetManualPrice,
  }
}
